import { useCallback } from "react";
import logger from "logging/logger";
import useUserProfile from "modules/userProfile/hooks/useUserProfile";
import { useAppDispatch } from "store/hooks";
import { IUser } from "modules/app/types";
import { User } from "firebase/auth";
import { setAuth, setCheckStatus } from "../redux/authSlice";

type UseAuthActionsValues = {
  completedCheckingAuth: () => void;
  setAuthUser: (userProfile: IUser) => void;
  retrieveUserProfile: () => Promise<void>;
  updateSAMLClaims: (firebaseUser: User) => Promise<void>;
};

const useAuthActions = (): UseAuthActionsValues => {
  const dispatch = useAppDispatch();
  const { getMyProfile, getUserClaims, updateMyProfile } = useUserProfile();

  const setAuthUser = useCallback((userProfile: IUser) => {
    dispatch(setAuth(userProfile));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retrieveUserProfile = useCallback(async () => {
    try {
      const userProfile = await getMyProfile();

      setAuthUser(userProfile);
    } catch (err) {
      logger.error(`[useAuthActions][retrieveUserProfile] error: ${err}`, {
        err,
      });
    }
  }, [getMyProfile, setAuthUser]);

  const completedCheckingAuth = useCallback(() => {
    dispatch(setCheckStatus(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateSAMLClaims = async (firebaseUser: User): Promise<void> => {
    try {
      const claimsToken = await firebaseUser.getIdToken();
      const userInfo = await getUserClaims(claimsToken);

      await updateMyProfile(userInfo);
      await retrieveUserProfile();
    } catch (error) {
      if (error instanceof Object) {
        logger.error("[updateSAMLClaims] error", error);
      }
    }
  };

  return {
    completedCheckingAuth,
    setAuthUser,
    retrieveUserProfile,
    updateSAMLClaims,
  };
};

export default useAuthActions;
